import React, { useState } from "react";
import { Grid, Typography, Box, Collapse, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import image from "./publishing.jpg"; // Update with correct path

const features = [
  {
    title: "AI-Assisted Submissions Welcome",
    content:
      "We recognize AI as a valid and evolving artistic tool. InkBowl Publishing accepts AI-assisted or AI-generated work when thoughtfully used, treating it as part of the creative process—no different from a camera, a stylus, or a brush. We evaluate all submissions for authenticity, intention, and artistic merit.",
  },
  {
    title: "Rolling Digital Publication",
    content:
      "InkBowl accepts submissions for digital publication on a rolling basis. Selected works are published throughout the year on our official platform, offering sustainable opportunities for recognition and visibility.",
  },
  {
    title: "Biannual Multimedia Magazine",
    content:
      "Our flagship magazine is published twice annually, digitally and in-print. Each issue features a limited selection of outstanding student work. Contributors have the opportunity to purchase a hard copy. Applications are rolling.",
  },
  {
    title: "Submit a Portfolio or Collection",
    content:
      "Writers and artists may submit cohesive collections—such as poetry cycles, visual art series, or multimedia portfolios—centered on a unifying theme or concept. Submissions that demonstrate strong conceptual continuity across multiple pieces are encouraged.",
  },
  {
    title: "Eligibility Criteria",
    content:
      "Applicants must be currently enrolled students. All submissions must be original, align with community guidelines, and reflect artistic or intellectual integrity. AI-assisted work is accepted when used intentionally as part of the creative process.",
  },
];

const FeatureInkBowlPublishing: React.FC = () => {
  const [hovered, setHovered] = useState<number | null>(null);
  const [clicked, setClicked] = useState<number | null>(null);

  return (
    <Box
      sx={{
        backgroundColor: "transparent",
        minHeight: "100vh",
        py: { xs: 4, md: 8 },
        px: { xs: 2, sm: 4, md: 0, xl: 8 },
      }}
    >
      <Grid
        container
        spacing={{ xs: 3, md: 6 }}
        sx={{
          height: "100%",
          width: "100%",
          margin: 0,
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        {/* Left Side: Full Height Image */}
        <Grid item xs={12} md={6} sx={{ order: { xs: 0, md: 0 } }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              height: {
                xs: "300px", // Slightly taller for mobile
                sm: "400px",
                md: "500px",
                lg: "600px",
              },
              width: "100%",
              mt: { xs: 0, md: 0 },
              mb: { xs: 4, md: 0 }, // More bottom margin on mobile
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={image}
              alt="image"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: { xs: 1, md: 2 },
                boxShadow: { xs: 2, md: 3 },
              }}
            />
          </Box>
        </Grid>

        {/* Right Side: Text Content */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 0, md: 3 }, // Remove top padding on mobile
            overflowY: "auto",
            order: { xs: 1, md: 1 }, // Text comes second on mobile
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.5rem" },
              lineHeight: { xs: 1.2, md: 1.3 },
            }}
          >
            InkBowl Publishing
          </Typography>

          <Typography
            variant="h5"
            sx={{
              mt: { xs: 1.5, md: 2 },
              fontWeight: "bold",
              fontSize: { xs: "1.2rem", sm: "1.4rem", md: "1.5rem" },
              lineHeight: { xs: 1.3, md: 1.4 },
              width: "35vw",
            }}
          >
            Selective literature & art publishing – digitally & in-print
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "1rem", md: "1.1rem" },
              mb: { xs: 3, md: 3 }, // More bottom margin on mobile
              lineHeight: { xs: 1.4, md: 1.5 },
              width: { xs: "65vw", md: "85vw" },
            }}
          >
            Online year-round and in our biannual multimedia magazine. <br />
            Acquire credentials and exposure for your work.
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mb: { xs: 2, md: 2 },
              fontSize: { xs: "0.9rem", md: "1rem" },
              lineHeight: { xs: 1.4, md: 1.5 },
              maxWidth: { xs: "65ch", md: "65ch" },
            }}
          >
            InkBowl Publishing offers students worldwide a distinguished
            platform to gain professional recognition through selective,
            merit-based publication. With thousands of applicants across 33
            countries, we consider a wide range of exceptional creative
            literature and multimedia art. Our editorial review process
            prioritizes quality, originality, and voice, ensuring each accepted
            piece reflects a high standard of craft.
            <br />
            <br />
            Published students receive a credible, portfolio-enhancing
            credential that strengthens applications, resumes, and professional
            visibility.
          </Typography>

          {/* Feature Blocks with Arrow – Transparent */}
          {features.map((feature, idx) => {
            const isExpanded = clicked === idx || hovered === idx; // Open on click or hover

            return (
              <Box
                key={idx}
                sx={{
                  p: { xs: 1.5, md: 2 },
                  mt: { xs: 1.5, md: 2 },
                  backgroundColor: "transparent",
                  borderRadius: 2,
                  transition: "all 0.3s ease-in-out",
                  cursor: "pointer",
                }}
                onMouseEnter={() => setHovered(idx)} // Desktop hover
                onMouseLeave={() => setHovered(null)} // Desktop hover
                onClick={() => {
                  // Mobile/Desktop click toggle
                  if (clicked === idx) {
                    setClicked(null); // Close if already open
                  } else {
                    setClicked(idx); // Open this one
                  }
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    minHeight: { xs: 44, md: "auto" }, // Minimum touch target size
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.95rem", md: "1rem" },
                      lineHeight: { xs: 1.3, md: 1.4 },
                      maxWidth: { xs: "80%", md: "85%" }, // Prevent text from overlapping button
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <IconButton
                    size="small"
                    disableRipple
                    sx={{
                      transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s ease-in-out",
                      minWidth: { xs: 44, md: "auto" }, // Minimum touch target size
                      minHeight: { xs: 44, md: "auto" },
                    }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </Box>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      fontSize: { xs: "0.85rem", md: "0.9rem" },
                      lineHeight: { xs: 1.4, md: 1.5 },
                      maxWidth: { xs: "100%", md: "95%" },
                    }}
                  >
                    {feature.content}
                  </Typography>
                </Collapse>
              </Box>
            );
          })}
        </Grid>
      </Grid>
    </Box>
  );
};

export default FeatureInkBowlPublishing;
